import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure, type LoadFailureKind } from '../lib/load-failure';
import { useAvailableForms } from './useAvailableForms';

/** How many custom-form tiles a Quick Actions grid draws before "More forms". */
export const MAX_FORM_TILES = 4;

export interface FormTile {
  id: string;
  name: string;
  /** Always `/forms/dynamic/:id` — the fill screen. */
  route: string;
}

interface TemplateRow {
  id: string;
  company_id?: string | null;
}

/**
 * The tenant's OWN custom forms, as Quick Actions tiles on the Safety pages.
 *
 * TWO READS, ONE QUESTION EACH. `/forms/available` decides what a crew may
 * open — field visibility (`active`), published, enabled in the library, the
 * tenant's verticals, a template with fields. It does NOT say who owns a
 * form: its rows carry no `company_id` or `is_system`, so a global template
 * and the tenant's own one look the same. `/safety/templates` (auth only, no
 * role gate) returns `ft.*`, and `company_id` there is the ownership fact. A
 * tile is a form on BOTH lists with a `company_id`.
 *
 * GLOBALS GET NO TILE. They stay in the full Forms list ("More forms"), where
 * they always were. A tenant with one form of its own and a dozen global ones
 * would otherwise see its own form pushed out of the row by forms it never
 * chose.
 *
 * Only forms that open the fill screen are tiles. A form whose route is a
 * built-in screen (JSA, incident) already has its own tile.
 *
 * NOTHING IS GUESSED ON A FAILURE: while either read is missing there are no
 * tiles, and `failure` says why, so a caller can say so rather than show a row
 * that silently lacks the tenant's forms.
 */
export function useTenantFormTiles() {
  const available = useAvailableForms();
  const owned = useQuery({
    queryKey: ['safety-templates-list', 'active'],
    queryFn: () => api.safety.templates({ status: 'active' }).then(r => (r.data?.data ?? r.data ?? []) as TemplateRow[]),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const failure: LoadFailureKind | null =
    (available.data === undefined ? queryLoadFailure(available) : null) ??
    (owned.data === undefined ? queryLoadFailure(owned) : null);
  const loading = !failure && (available.data === undefined || owned.data === undefined);

  const all: FormTile[] = [];
  if (available.data && owned.data) {
    const tenantIds = new Set(owned.data.filter(t => !!t.company_id).map(t => t.id));
    const seen = new Set<string>();
    for (const group of available.data) {
      for (const f of group.forms) {
        if (seen.has(f.id) || !tenantIds.has(f.id)) continue;
        if (f.comingSoon || f.disabledReason) continue;
        if (!f.route?.startsWith('/forms/dynamic/')) continue;
        seen.add(f.id);
        all.push({ id: f.id, name: f.name, route: f.route });
      }
    }
  }

  return {
    tiles: all.slice(0, MAX_FORM_TILES),
    /** More than fit: the caller draws a "More forms" tile to the full list. */
    hasMore: all.length > MAX_FORM_TILES,
    total: all.length,
    loading,
    failure,
    failureQuery: available.data === undefined ? available : owned,
  };
}
